import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";

export const AssistantUnreadContext = createContext(null);

const clampInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

function readStoredSessionId() {
  try {
    return localStorage.getItem("assistant_session_id") || "";
  } catch {
    return "";
  }
}

function readNotifiedIds() {
  try {
    const raw = localStorage.getItem("assistant_notified_outbound_ids") || "[]";
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export const AssistantUnreadProvider = ({
  children,
  enabled = true,
  intervalMs = 30_000,
}) => {
  const queryClient = useQueryClient();
  const [sessionId, setSessionId] = useState(() => readStoredSessionId());
  const sessionIdRef = useRef(sessionId);
  const notifiedIdsRef = useRef(readNotifiedIds());
  const [latestNotification, setLatestNotification] = useState(null);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    const refreshSession = (nextSessionId) => {
      const normalizedSessionId = String(nextSessionId || "").trim();
      if (normalizedSessionId !== sessionIdRef.current) {
        sessionIdRef.current = normalizedSessionId;
        setSessionId(normalizedSessionId);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["assistant_unread_summary", normalizedSessionId],
      });
    };
    const syncSession = (event) => {
      const nextSessionId =
        String(event?.detail?.sessionId || "").trim() || readStoredSessionId();
      refreshSession(nextSessionId);
    };
    const refresh = () => {
      refreshSession(readStoredSessionId());
    };

    window.addEventListener("vg-assistant-session-updated", syncSession);
    window.addEventListener("vg-assistant-unread-refresh", refresh);

    return () => {
      window.removeEventListener("vg-assistant-session-updated", syncSession);
      window.removeEventListener("vg-assistant-unread-refresh", refresh);
    };
  }, [queryClient]);

  const { data } = useQuery({
    queryKey: ["assistant_unread_summary", sessionId],
    enabled: !!enabled,
    queryFn: async () => {
      const params = {};
      if (sessionId) {
        params.session_id = sessionId;
      }
      const res = await runBackgroundRequest(() => (
        api.get("/assistant/unread-summary/", {
          params,
          meta: {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
            suppressGlobalError: true,
          },
        })
      ));
      return res?.data || { unread_count: 0, session_id: sessionId || "", latest_unread: null };
    },
    refetchInterval: intervalMs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const nextSessionId = String(data?.session_id || "").trim();
    if (!nextSessionId || nextSessionId === sessionId) return;
    setSessionId(nextSessionId);
    try {
      localStorage.setItem("assistant_session_id", nextSessionId);
    } catch {
      // ignore
    }
  }, [data?.session_id, sessionId]);

  useEffect(() => {
    const latestUnread = data?.latest_unread || null;
    const latestId = Number(latestUnread?.id || 0);
    if (!latestId) return;
    if (notifiedIdsRef.current.has(latestId)) return;

    notifiedIdsRef.current.add(latestId);
    try {
      localStorage.setItem(
        "assistant_notified_outbound_ids",
        JSON.stringify(Array.from(notifiedIdsRef.current))
      );
    } catch {
      // ignore
    }
    setLatestNotification(latestUnread);
  }, [data?.latest_unread]);

  const value = useMemo(
    () => ({
      unreadCount: clampInt(data?.unread_count),
      sessionId: String(data?.session_id || sessionId || ""),
      latestNotification,
      clearLatestNotification: () => setLatestNotification(null),
    }),
    [data?.session_id, data?.unread_count, latestNotification, sessionId]
  );

  return (
    <AssistantUnreadContext.Provider value={value}>
      {children}
    </AssistantUnreadContext.Provider>
  );
};

export const useAssistantUnread = () => {
  const ctx = useContext(AssistantUnreadContext);
  return (
    ctx || {
      unreadCount: 0,
      sessionId: "",
    }
  );
};
